import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppRole } from '../enums/app-role.enum';
import { Profile } from '../models/profile.model';
import { AuthService } from './auth.service';
import { SupabaseService } from './supabase.service';

/** User profile and role management service. */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private signedAvatarCache = new Map<string, { url: string | null; expiresAt: number }>();
  private static readonly SIGNED_AVATAR_CACHE_TTL_MS = 55 * 60 * 1000;

  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);
  private currentProfileSubject = new BehaviorSubject<Profile | null>(null);
  private currentRolesSubject = new BehaviorSubject<AppRole[]>([]);

  readonly currentProfile$ = this.currentProfileSubject.asObservable();
  readonly currentRoles$ = this.currentRolesSubject.asObservable();

  get currentProfile(): Profile | null {
    return this.currentProfileSubject.value;
  }

  get currentRoles(): AppRole[] {
    return this.currentRolesSubject.value;
  }

  constructor() {
    this.auth.session$.subscribe((session) => {
      if (session) {
        void this.loadCurrentUser();
      } else {
        this.clearCache();
      }
    });
  }

  /** Loads the profile and roles of the signed-in user. */
  async loadCurrentUser(): Promise<void> {
    const { data: authData } = await this.supabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) {
      this.currentProfileSubject.next(null);
      this.currentRolesSubject.next([]);
      return;
    }
    const [profile, roles] = await Promise.all([this.getByUserId(userId), this.getRoles(userId)]);
    this.currentProfileSubject.next(profile);
    this.currentRolesSubject.next(roles);
  }

  /** Fetches a profile by Supabase user ID. */
  async getByUserId(userId: string): Promise<Profile | null> {
    const { data, error } = await this.supabase.from('profiles').select('*').eq('user_id', userId).maybeSingle();
    if (error) throw error;
    return data ? Profile.fromRow(data) : null;
  }

  /** Lists all profiles sorted by last name. */
  async list(): Promise<Profile[]> {
    const { data, error } = await this.supabase.from('profiles').select('*').order('last_name');
    if (error) throw error;
    return (data ?? []).map(Profile.fromRow);
  }

  /** Fetches a profile by ID. */
  async get(id: string): Promise<Profile | null> {
    const { data, error } = await this.supabase.from('profiles').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? Profile.fromRow(data) : null;
  }

  /** Updates profile fields. */
  async update(id: string, profile: Partial<Profile>): Promise<void> {
    const { error } = await this.supabase.from('profiles').update({
      first_name: profile.firstName,
      last_name: profile.lastName,
      display_name: profile.displayName,
      status: profile.status as never,
      avatar_url: profile.avatarUrl,
    }).eq('id', id);
    if (error) throw error;
  }

  /** Fetches the application roles of a user. */
  async getRoles(userId: string): Promise<AppRole[]> {
    const { data, error } = await this.supabase.from('user_roles').select('role').eq('user_id', userId);
    if (error) throw error;
    return (data ?? []).map((r) => r.role as AppRole);
  }

  /** Assigns or updates a role for a user (atomic RPC). */
  async setRole(userId: string, role: AppRole): Promise<void> {
    const { data: existing, error: fetchError } = await this.supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    if (fetchError) throw fetchError;

    const rows = existing ?? [];
    if (rows.length === 1 && rows[0].role === role) return;

    const { error } = await this.supabase.rpc('set_user_role', {
      _user_id: userId,
      _role: role,
    });
    if (error) throw error;
  }

  /** Uploads an avatar and updates the associated profile. */
  async uploadAvatar(userId: string, file: File): Promise<string> {
    // The stored name is generated: a user file name carrying « + », « # », accents or narrow spaces (macOS screenshots) is
    // refused by the storage or gives a signed URL that does not load, which showed as a broken image.
    const extension = (/\.(png|jpe?g|gif|webp|avif|heic|svg)$/i.exec(file.name)?.[1] ?? /^image\/([a-z0-9]+)/i.exec(file.type)?.[1] ?? 'png').toLowerCase();
    const path = `${userId}/${Date.now()}.${extension}`;
    const { error: uploadError } = await this.supabase.storage('avatars').upload(path, file, { upsert: true });
    if (uploadError) throw uploadError;
    const { data } = this.supabase.storage('avatars').getPublicUrl(path);
    const profile = await this.getByUserId(userId);
    if (profile) await this.update(profile.id, { avatarUrl: path });
    return data.publicUrl;
  }

  /** Updates a user's presence status (manual — not overwritten by Outlook cron). */
  async updateStatusByUserId(userId: string, status: string): Promise<void> {
    const { error } = await this.supabase
      .from('profiles')
      .update({ status: status as never, status_source: 'manual' })
      .eq('user_id', userId);
    if (error) throw error;
  }

  /** Activates or deactivates a user account. */
  async toggleActive(userId: string, isActive: boolean): Promise<void> {
    const { error } = await this.supabase.from('profiles').update({ is_active: isActive }).eq('user_id', userId);
    if (error) throw error;
  }

  /** Builds a userId → primary role map for all users. */
  async listRolesMap(): Promise<Map<string, AppRole>> {
    const { data, error } = await this.supabase.from('user_roles').select('user_id, role');
    if (error) throw error;
    const map = new Map<string, AppRole>();
    for (const r of data ?? []) {
      const cur = map.get(r.user_id);
      if (!cur || r.role === 'superadmin' || r.role === 'admin') map.set(r.user_id, r.role);
    }
    return map;
  }

  /** Generates a temporary signed URL for a stored avatar. */
  async getSignedAvatarUrl(path: string, forceRefresh = false): Promise<string | null> {
    if (forceRefresh) {
      this.signedAvatarCache.delete(path);
    } else {
      const cached = this.getCachedSignedAvatarUrl(path);
      if (cached !== undefined) return cached;
    }

    const { data, error } = await this.supabase.storage('avatars').createSignedUrl(path, 3600);
    const url = error ? null : data.signedUrl;
    this.setCachedSignedAvatarUrl(path, url);
    return url;
  }

  private getCachedSignedAvatarUrl(path: string): string | null | undefined {
    const entry = this.signedAvatarCache.get(path);
    if (!entry) return undefined;
    if (Date.now() >= entry.expiresAt) {
      this.signedAvatarCache.delete(path);
      return undefined;
    }
    return entry.url;
  }

  private setCachedSignedAvatarUrl(path: string, url: string | null): void {
    this.signedAvatarCache.set(path, {
      url,
      expiresAt: Date.now() + ProfileService.SIGNED_AVATAR_CACHE_TTL_MS,
    });
  }

  /** Resets the current profile and roles cache. */
  clearCache(): void {
    this.currentProfileSubject.next(null);
    this.currentRolesSubject.next([]);
  }
}
